/**
 * Plantillas de texto libre de los documentos: sustitución segura de
 * variables `{{grupo.nombre}}` y detección de variables desconocidas o mal
 * escritas.
 *
 * Seguridad: no se evalúa código ni expresiones. Una variable es solo un
 * nombre de un catálogo cerrado; la sustitución se hace en una sola pasada,
 * así que un valor que contenga `{{…}}` (p. ej. el nombre de un cliente) se
 * imprime tal cual y nunca se vuelve a interpretar.
 */
/** Apertura y cierre de una variable en el texto. */
export const TEMPLATE_OPEN = '{{';
export const TEMPLATE_CLOSE = '}}';
/**
 * Una variable: `{{`, espacios opcionales, cualquier texto sin llaves y
 * `}}`. El contenido se valida aparte con `TEMPLATE_VARIABLE_NAME_PATTERN`
 * para poder informar el error exacto al administrador.
 */
const TEMPLATE_TOKEN_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;
/** Nombre válido: segmentos en minúsculas y guion bajo separados por punto (`cliente.nombre`). */
export const TEMPLATE_VARIABLE_NAME_PATTERN = /^[a-z_]+(?:\.[a-z_]+)*$/;
/** Tipos de problema de una plantilla (para mensajes y pruebas). */
export const TEMPLATE_ISSUE_TYPES = { unknown: 'unknown', malformed: 'malformed' };
/** Escribe una variable en su forma canónica: `cliente.nombre` → `{{cliente.nombre}}`. */
export function templateToken(variable) {
    return `${TEMPLATE_OPEN}${variable}${TEMPLATE_CLOSE}`;
}
/** Nombres de variable (bien escritos) que aparecen en el texto, sin repetir y en orden de aparición. */
export function extractTemplateVariables(text) {
    const names = new Set();
    for (const match of text.matchAll(TEMPLATE_TOKEN_PATTERN)) {
        const name = match[1] ?? '';
        if (TEMPLATE_VARIABLE_NAME_PATTERN.test(name))
            names.add(name);
    }
    return [...names];
}
/**
 * Revisa un texto contra el conjunto de variables permitidas y devuelve los
 * problemas encontrados (sin repetir). Un texto sin llaves no tiene problemas.
 */
export function findTemplateIssues(text, allowed) {
    const issues = [];
    const reported = new Set();
    const report = (key, issue) => {
        if (reported.has(key))
            return;
        reported.add(key);
        issues.push(issue);
    };
    for (const match of text.matchAll(TEMPLATE_TOKEN_PATTERN)) {
        const name = match[1] ?? '';
        if (!TEMPLATE_VARIABLE_NAME_PATTERN.test(name))
            report(`malformed:${match[0]}`, { type: 'malformed', fragment: match[0] });
        else if (!allowed.has(name))
            report(`unknown:${name}`, { type: 'unknown', variable: name });
    }
    // Lo que queda sin variables válidas no debe tener llaves dobles sueltas.
    const remainder = text.replace(TEMPLATE_TOKEN_PATTERN, '');
    if (remainder.includes(TEMPLATE_OPEN) || remainder.includes(TEMPLATE_CLOSE)) {
        report('malformed:unclosed', { type: 'malformed', fragment: unclosedFragment(remainder) });
    }
    return issues;
}
/** Longitud del fragmento que se muestra al señalar llaves sin cerrar. */
const UNCLOSED_FRAGMENT_LENGTH = 24;
/** Fragmento alrededor de la primera llave doble suelta, para el mensaje de error. */
function unclosedFragment(text) {
    const openAt = text.indexOf(TEMPLATE_OPEN);
    const start = openAt >= 0 ? openAt : Math.max(0, text.indexOf(TEMPLATE_CLOSE) - UNCLOSED_FRAGMENT_LENGTH);
    return text.slice(start, start + UNCLOSED_FRAGMENT_LENGTH);
}
/**
 * Sustituye cada `{{variable}}` por su valor. Es una función pura: no evalúa
 * nada, no modifica los valores y hace una sola pasada (un valor con llaves
 * no se vuelve a interpretar).
 *
 * @example renderTemplate('Hola {{cliente.nombre}}', { 'cliente.nombre': 'Ana' }) → 'Hola Ana'
 */
export function renderTemplate(text, values, options = {}) {
    return text.replace(TEMPLATE_TOKEN_PATTERN, (token, rawName) => {
        const value = Object.hasOwn(values, rawName) ? values[rawName] : undefined;
        if (value !== undefined && value !== null && value.trim() !== '')
            return value;
        return options.fallback ? options.fallback(rawName, token) : token;
    });
}
//# sourceMappingURL=template.js.map